import React from 'react'
import 'components/ad-preview/ad-preview.scss'
import './CreativeJourney.scss'
import OpenInNewIcon from '@material-ui/icons/OpenInNew'
import { AdCreative, Business, LeadCard } from '@marketing-milk/interfaces'
import { LeadCardPreview } from '../../lead-cards/lead-card-preview/LeadCardPreview'
import { leadCardDTOWire } from '../../lead-cards/wizard/lead-card.wire'
import { radius, SurfacePalette, type } from '../tokens'

/**
 * What happens after the client taps the ad — the two cases are mutually
 * exclusive per creative: a lead-form ad opens its lead form, anything else
 * navigates to its landing page.
 */
export type CreativeJourney =
  | { kind: 'lead-form'; leadCard: LeadCard }
  | { kind: 'destination'; url: string; label: string }

/** Hostname for a labelless destination; tolerates scheme-less stored URLs. */
function hostOf(url: string): string {
  try {
    return new URL(/^https?:\/\//i.test(url) ? url : `https://${url}`).hostname
  } catch {
    return url
  }
}

/** Scheme-less stored URLs would resolve as relative paths in an <a>. */
function toHref(url: string): string {
  return /^https?:\/\//i.test(url) ? url : `https://${url}`
}

/**
 * Resolve a creative's post-click journey. Lead form wins when present (its CTA
 * opens the form, not the link). The landing page comes from `linkWebsiteURL` —
 * NOT `destinationURL`, which is the Facebook post permalink, not the click
 * destination. Creatives with neither (e.g. engagement ads) have no journey.
 */
export function getCreativeJourney(creative: AdCreative): CreativeJourney | undefined {
  if (creative.leadCardUsed) return { kind: 'lead-form', leadCard: creative.leadCardUsed }
  if (creative.linkWebsiteURL) {
    return {
      kind: 'destination',
      url: creative.linkWebsiteURL,
      label: creative.linkDisplayURL || hostOf(creative.linkWebsiteURL),
    }
  }
  return undefined
}

const Eyebrow = ({ palette, children }: { palette: SurfacePalette; children: React.ReactNode }) => (
  <span
    style={{
      fontFamily: type.family.sans,
      fontSize: type.size.eyebrow,
      fontWeight: type.weight.semibold,
      letterSpacing: '0.08em',
      textTransform: 'uppercase',
      color: palette.textFaint,
    }}
  >
    {children}
  </span>
)

/**
 * Clickable landing-page card. Shows the clean display label but links to the
 * real destination URL (UTM params and all) in a new tab — the value is the AE
 * proving in-meeting that the ad goes to the right place and loads fast, so the
 * href must be the true destination. The click blurs the anchor so keyboard
 * focus returns to the deck (arrow-key navigation) when the tab is closed.
 */
export function DestinationLinkCard({
  url,
  label,
  palette,
}: {
  url: string
  label: string
  palette: SurfacePalette
}) {
  return (
    <a
      href={toHref(url)}
      target="_blank"
      rel="noopener noreferrer"
      title={toHref(url)}
      onClick={e => e.currentTarget.blur()}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 12,
        padding: '12px 14px',
        borderRadius: radius.md,
        border: `1px solid ${palette.hairline}`,
        background: palette.surface,
        textDecoration: 'none',
      }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
        <Eyebrow palette={palette}>Landing page</Eyebrow>
        <span
          style={{
            fontFamily: type.family.sans,
            fontSize: type.size.body,
            fontWeight: type.weight.medium,
            color: palette.accent,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {label}
        </span>
      </div>
      <OpenInNewIcon style={{ fontSize: 16, color: palette.textMuted, flexShrink: 0 }} />
    </a>
  )
}

interface PanelProps {
  journey: CreativeJourney
  business?: Business
  palette: SurfacePalette
  /**
   * Slim single-row rendering for hosts without column room (the dynamic asset
   * pool): the destination link card as a strip, and lead-form as a labeled note
   * instead of the embedded phone preview.
   */
  compact?: boolean
}

/**
 * "What happens after the tap" panel shown beside the ad preview. A lead-form
 * creative embeds the interactive `LeadCardPreview` phone (its built-in section
 * pager steps through questions → prefill → privacy → thank-you, so the client
 * can approve every screen); a destination creative gets the clickable link
 * card. The lead card arrives fully hydrated on the sprint creative
 * (`leadCardUsed`), adapted via the existing `leadCardDTOWire`.
 */
export function CreativeJourneyPanel({ journey, business, palette, compact }: PanelProps) {
  if (journey.kind === 'destination') {
    return <DestinationLinkCard url={journey.url} label={journey.label} palette={palette} />
  }

  if (compact || !business) {
    // No room (or no loaded business) for the phone preview — name the form so
    // the journey is still visible.
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          padding: '12px 14px',
          borderRadius: radius.md,
          border: `1px solid ${palette.hairline}`,
          background: palette.surface,
        }}
      >
        <Eyebrow palette={palette}>Lead form</Eyebrow>
        <span
          style={{
            fontFamily: type.family.sans,
            fontSize: type.size.body,
            fontWeight: type.weight.medium,
            color: palette.text,
          }}
        >
          {journey.leadCard.formName}
        </span>
      </div>
    )
  }

  // Unlabelled on purpose: the caller reaches this step from a control that names
  // it and stays on screen, so a caption only restates the segment you pressed —
  // and inside a scale-to-fit frame it is not free, it costs the phone the height
  // it occupies.
  return (
    <div
      className="creative-journey-phone"
      style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}
    >
      <LeadCardPreview leadCard={leadCardDTOWire(journey.leadCard)} business={business} />
    </div>
  )
}
